import { useMemo, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BENDER_OPTIONS,
  CONDUIT_OPTIONS,
  formatDefaultConduitLabel,
  getConduitOptionsForDefaults,
  getMaterialOptionsForBender,
  setAppSetting,
  useAppSettings,
  type DefaultBender,
  type DefaultConduit,
  type DefaultMaterial,
} from '../lib/app-settings.js';
import { completeOnboarding, getSafeOnboardingReturnPath } from '../lib/onboarding.js';

const POPULAR_REGION = 'Popular on jobsite';

export default function OnboardingScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const settings = useAppSettings();
  const returnPath = getSafeOnboardingReturnPath(
    (location.state as { returnTo?: unknown } | null)?.returnTo,
  );
  const materialOptions = useMemo(
    () => getMaterialOptionsForBender(settings.defaultBender),
    [settings.defaultBender],
  );
  const conduitOptions = useMemo(
    () => getConduitOptionsForDefaults(settings.defaultBender, settings.defaultMaterial),
    [settings.defaultBender, settings.defaultMaterial],
  );

  function finish() {
    completeOnboarding();
    navigate(returnPath, { replace: true });
  }

  return (
    <main style={onboardingShellStyle}>
      <div style={topBarStyle}>
        <span style={brandStyle}>
          True<span style={{ color: 'var(--color-accent)' }}>bend</span>
        </span>
        <span className="mono" style={stepStyle}>
          Setup
        </span>
      </div>

      <section style={heroStyle}>
        <div style={eyebrowStyle}>Daily bender</div>
        <h1 style={titleStyle}>Pick your daily bender.</h1>
        <p style={subtitleStyle}>
          {formatDefaultConduitLabel(settings.defaultConduit, settings.defaultMaterial)} {'\u00b7'}{' '}
          {settings.defaultBender}
        </p>
      </section>

      <BenderPreview bender={settings.defaultBender} />

      <section aria-label={POPULAR_REGION} style={benderListStyle}>
        {BENDER_OPTIONS.map((option) => (
          <BenderOption
            key={option}
            option={option}
            active={option === settings.defaultBender}
            onPick={() => setAppSetting('defaultBender', option)}
          />
        ))}
      </section>

      <section style={defaultsGridStyle}>
        <SelectPill
          label="Conduit"
          value={settings.defaultConduit}
          options={conduitOptions.length > 0 ? conduitOptions : CONDUIT_OPTIONS}
          optionLabels={(conduitOptions.length > 0 ? conduitOptions : CONDUIT_OPTIONS).map(
            (option) => formatDefaultConduitLabel(option, settings.defaultMaterial),
          )}
          onChange={(value) => setAppSetting('defaultConduit', value as DefaultConduit)}
        />
        <SelectPill
          label="Material"
          value={settings.defaultMaterial}
          options={materialOptions}
          onChange={(value) => setAppSetting('defaultMaterial', value as DefaultMaterial)}
        />
      </section>

      <div style={actionBarStyle}>
        <button type="button" onClick={finish} style={skipButtonStyle}>
          Skip
        </button>
        <button type="button" onClick={finish} style={continueButtonStyle}>
          Continue {'\u2192'}
        </button>
      </div>
    </main>
  );
}

function BenderPreview({ bender }: { bender: DefaultBender }) {
  return (
    <div style={previewFrameStyle} aria-label={`${bender} preview`}>
      <svg viewBox="0 0 360 132" role="img" aria-label="Active bender preview" style={svgStyle}>
        <g opacity="0.25" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 6 }).map((_, i) => (
            <line key={`h-${i}`} x1="20" y1={20 + i * 18} x2="340" y2={20 + i * 18} />
          ))}
          {Array.from({ length: 9 }).map((_, i) => (
            <line key={`v-${i}`} x1={36 + i * 36} y1="16" x2={36 + i * 36} y2="112" />
          ))}
        </g>
        <path
          d="M 42 94 L 128 94 Q 144 94 154 78 L 192 24 Q 202 10 218 10 L 318 10"
          fill="none"
          stroke="var(--color-diagram-pipe-shadow)"
          strokeWidth="18"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.34"
          transform="translate(0 8)"
        />
        <path
          d="M 42 94 L 128 94 Q 144 94 154 78 L 192 24 Q 202 10 218 10 L 318 10"
          fill="none"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M 128 94 Q 144 94 154 78 L 192 24 Q 202 10 218 10"
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="128" cy="94" r="7" fill="var(--color-measure)" />
        <circle cx="218" cy="10" r="7" fill="var(--color-measure)" />
        <text
          x="180"
          y="122"
          fill="var(--color-text-muted)"
          textAnchor="middle"
          fontSize="11"
          fontWeight="800"
        >
          active bender preview
        </text>
      </svg>
    </div>
  );
}

function BenderOption({
  option,
  active,
  onPick,
}: {
  option: DefaultBender;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onPick}
      style={active ? benderOptionActiveStyle : benderOptionStyle}
    >
      <span aria-hidden="true" style={active ? radioActiveStyle : radioStyle}>
        {active && <span style={radioDotStyle} />}
      </span>
      <span style={optionTitleStyle}>{option}</span>
      {active && <span style={yourPickStyle}>Your pick</span>}
    </button>
  );
}

function SelectPill<T extends string>({
  label,
  value,
  options,
  optionLabels,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  optionLabels?: readonly string[];
  onChange: (value: T) => void;
}) {
  return (
    <label style={selectPillStyle}>
      <span style={selectLabelStyle}>{label}</span>
      <span style={selectValueStyle}>
        {optionLabels?.[options.indexOf(value)] ?? value}
        <select
          aria-label={label}
          value={value}
          onChange={(event) => onChange(event.target.value as T)}
          style={selectControlStyle}
        >
          {options.map((option, index) => (
            <option key={option} value={option}>
              {optionLabels?.[index] ?? option}
            </option>
          ))}
        </select>
      </span>
    </label>
  );
}

const onboardingShellStyle: CSSProperties = {
  minHeight: '100dvh',
  maxWidth: 430,
  margin: '0 auto',
  padding: 'var(--space-4)',
  display: 'grid',
  gridTemplateRows: 'auto auto auto auto auto auto',
  alignContent: 'start',
  gap: 'var(--space-4)',
};

const topBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 36,
};

const brandStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-lg)',
  fontWeight: 900,
};

const stepStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: 'var(--text-xs)',
  fontWeight: 850,
  textTransform: 'uppercase',
};

const heroStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-1)',
};

const eyebrowStyle: CSSProperties = {
  color: 'var(--color-accent)',
  fontSize: '0.68rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const titleStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-3xl)',
  fontWeight: 900,
  lineHeight: 1.02,
};

const subtitleStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  margin: 0,
  fontSize: 'var(--text-sm)',
};

const previewFrameStyle: CSSProperties = {
  background: 'var(--color-diagram-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
  padding: 'var(--space-2)',
};

const svgStyle: CSSProperties = {
  width: '100%',
  height: 'auto',
  display: 'block',
};

const benderListStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
  alignSelf: 'start',
  gridAutoRows: 'minmax(56px, auto)',
};

const benderOptionStyle: CSSProperties = {
  width: '100%',
  minHeight: 56,
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3)',
  display: 'grid',
  gridTemplateColumns: '26px 1fr auto',
  alignItems: 'center',
  gap: 'var(--space-3)',
  textAlign: 'left',
};

const benderOptionActiveStyle: CSSProperties = {
  ...benderOptionStyle,
  background: 'rgba(249, 115, 22, 0.1)',
  borderColor: 'var(--color-accent)',
};

const radioStyle: CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  border: '2px solid var(--color-text-muted)',
  display: 'grid',
  placeItems: 'center',
};

const radioActiveStyle: CSSProperties = {
  ...radioStyle,
  borderColor: 'var(--color-accent)',
};

const radioDotStyle: CSSProperties = {
  width: 9,
  height: 9,
  borderRadius: '50%',
  background: 'var(--color-accent)',
};

const optionTitleStyle: CSSProperties = {
  fontWeight: 850,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const yourPickStyle: CSSProperties = {
  color: 'var(--color-accent)',
  fontSize: '0.65rem',
  fontWeight: 900,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const defaultsGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'var(--space-2)',
};

const selectPillStyle: CSSProperties = {
  minHeight: 58,
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-2) var(--space-3)',
  display: 'grid',
  gap: '2px',
};

const selectLabelStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: '0.65rem',
  fontWeight: 850,
  textTransform: 'uppercase',
};

const selectValueStyle: CSSProperties = {
  position: 'relative',
  minWidth: 0,
  fontSize: 'var(--text-sm)',
  fontWeight: 900,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const selectControlStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  opacity: 0,
  cursor: 'pointer',
};

const actionBarStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '0.8fr 1.4fr',
  gap: 'var(--space-2)',
  paddingTop: 'var(--space-2)',
};

const skipButtonStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontWeight: 850,
};

const continueButtonStyle: CSSProperties = {
  color: 'var(--color-text-on-orange)',
  background: 'var(--color-accent)',
  border: '1px solid var(--color-accent)',
  borderRadius: 'var(--radius-md)',
  fontWeight: 900,
  boxShadow: '0 14px 30px rgba(249, 115, 22, 0.26)',
};
